import { AttachmentService } from "../../services/attachment-service";
import { IEventAggregator, bindable, inject } from "aurelia";
import { getLogger } from "../../common/logging";
import { I18N } from "@aurelia/i18n";
import numeral from "numeral";
import { isHeic } from "../../common/heic";
import { formatsToSaveAs } from "../../common/file-formats";

import { AttachmentChanged } from "../../messages/attachment-changed";

let log = getLogger("attachment");

@inject(Element, IEventAggregator, AttachmentService, I18N)
export class AttachmentCustomElement {
  @bindable referenceId;
  @bindable accept;
  @bindable maxFileSize;
  @bindable name;
  @bindable maxPages;
  @bindable description;
  @bindable hideDescriptionInput;
  @bindable index;
  @bindable type;
  @bindable editable;
  @bindable placeholder;
  @bindable enabled;
  // Opt-in: hides the "Max X MB" text. The size limit itself still applies.
  @bindable hideMaxFileSize;

  constructor(element, ea, attachmentService, i18n) {
    this.element = element;
    this.ea = ea;
    this.attachmentService = attachmentService;
    this.i18n = i18n;

    this.uploadedAt = null;
    this.attachmentId = null;
    this.pages = null;
    this.type = "";
    this.progress = null;
    this.isUploading = false;
    this.hasAttachment = false;
    this.enabled = true;
    this.placeholder = "";
    if (this.description == null) this.description = "";
    if (!this.maxFileSize) this.maxFileSize = 20000000; //20MB
  }

  get showMaxFileSize() {
    if (this.hideMaxFileSize === true || this.hideMaxFileSize === "true") return false;
    return !!this.maxFileSize;
  }

  attached() {
    this.wireInputHandlers();

    this.attachmentService
      .get(this.referenceId, this.name)
      .then((attachment) => this.setAttachment(attachment))
      .catch((e) => log.error("failed fetching attachments:" + e.message));
  }

  wireInputHandlers() {
    this.browse.addEventListener('change', (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (file) this.handleSelectedFile(file);
    });
  }

  async handleSelectedFile(file) {
    this.error = "";
    // A HEIC image can't be printed, whatever its name says, so it is refused in every field.
    if (await isHeic(file).catch(() => false)) {
      this.error = this.i18n.tr("attachment.heicNotAccepted");
      return;
    }

    this.isAcceptableSize = this.isOfAcceptableSize(file.size);
    this.isAcceptableType = this.isOfAcceptableType(file.type, this.accept);

    if (!this.isAcceptableSize) {
      this.error = this.i18n.tr("attachment.fileToLarge", {
        size: numeral(file.size).format("0.0 b"),
        maxSize: numeral(this.maxFileSize).format("0.0 b"),
      });
      return;
    }
    if (!this.isAcceptableType) {
      this.error = this.i18n.tr("attachment.wrongFileType", {
        fileType: file.type,
        accepted: this.accept,
        interpolation: { escapeValue: false },
      });
      return;
    }

    if (!this.type) {
      this.type = this.getType(file.type);
    }

    this.isUploading = true;
    this.uploadViaPresignedUrl(file);
  }

  descriptionChanged() {
    if (!this.hasAttachment) return;
    this.attachmentService.save(this.attachmentId, this.description);
  }

  setAttachment(attachment) {
    if (attachment === null) return;
    this.hasAttachment = true;
    this.attachmentId = attachment.id;
    this.attachmentUrl = this.attachmentService.getAttachmentUrl(
      this.attachmentId
    );
    this.uploadedAt = attachment.uploaded;
    this.pages = attachment.pages;
    this.fileSize = attachment.length;
    this.originalName = attachment.original_name;
    this.description = attachment.description;
    this.ea.publish(new AttachmentChanged(this.attachmentId, this.name, true));
  }

  clearAttachment() {
    this.hasAttachment = false;
    this.attachmentUrl = null;
    this.attachmentId = null;
    this.uploadedAt = null;
    this.pages = null;
    this.fileSize = null;
    this.type = "";
    this.ea.publish(new AttachmentChanged(this.attachmentId, this.name, false));
  }

  async uploadViaPresignedUrl(file) {
    const fileType = this.type || this.getType(file.type);

    try {
      if (this.attachmentId) {
        await this.attachmentService.remove(this.attachmentId);
      }

      const presignResult = await this.attachmentService.presign(
        file.name,
        this.name,
        this.referenceId,
        file.type,
        this.maxFileSize
      );

      await this.attachmentService.uploadToPresignedPost(
        presignResult.url,
        presignResult.fields,
        file,
        (progress) => { this.progress = progress; }
      );

      const confirmResult = await this.attachmentService.confirm(
        presignResult.tempKey,
        this.name,
        file.name,
        this.referenceId,
        this.description,
        this.index,
        fileType,
        this.maxPages
      );

      this.isUploading = false;

      if (confirmResult.success) {
        this.hasAttachment = true;
        this.uploadedAt = new Date().toISOString();
        this.attachmentId = confirmResult.id;
        this.attachmentUrl = this.attachmentService.getAttachmentUrl(this.attachmentId);
        this.pages = confirmResult.pages;
        this.contentType = file.type;
        this.fileSize = file.size;
        this.originalName = file.name;
        this.ea.publish(new AttachmentChanged(this.attachmentId, this.name, true));
        this.moveFocusTo(this.removeButton);
      } else {
        this.clearAttachment();
        if (confirmResult.error === "pages") {
          this.error = this.i18n.tr("attachment.tooManyPages", {
            pages: confirmResult.pages,
            maxPages: this.maxPages,
          });
        } else if (confirmResult.error === "format") {
          this.error = this.i18n.tr("attachment.unreadableFile", {
            accepted: formatsToSaveAs(this.accept, this.i18n.tr("attachmentV2.or")),
            interpolation: { escapeValue: false },
          });
        } else {
          this.error = this.i18n.tr("attachment.unknownError");
        }
      }
    } catch (e) {
      log.error("Presigned upload failed:", e);
      this.isUploading = false;
      this.clearAttachment();

      if (e.statusCode && e.response) {
        try {
          const body = JSON.parse(e.response);
          if (Array.isArray(body) && body.length > 0 && body[0].type === "invalid-pdf") {
            this.error = this.i18n.tr("attachment.invalidpdf", { msg: body[0].message });
          } else {
            this.error = this.i18n.tr("attachment.unknownError");
          }
        } catch (_) {
          this.error = this.i18n.tr("attachment.unknownError");
        }
      } else {
        this.error = this.i18n.tr("attachment.unknownError");
      }
    }
  }

  isOfAcceptableSize(size) {
    return !this.maxFileSize || size < this.maxFileSize;
  }

  isOfAcceptableType(fileType, types) {
    return types
      .split(",")
      .some(
        (type) =>
          type === fileType ||
          (type.split("/")[0] === fileType.split("/")[0] &&
            type.split("/")[1] === "*")
      );
  }

  remove() {
    if (this.attachmentId) {
      this.attachmentService.remove(this.attachmentId);
      this.clearAttachment();
      this.moveFocusTo(this.browse);
    }
  }

  // Knappen som användes döljs när en fil laddats upp eller tagits bort. Fokus flyttas till den som
  // tar dess plats, annars hamnar det på sidans början (WCAG 2.4.3). Visningen ändras först i nästa task.
  // Har användaren gått vidare till ett annat fält medan filen laddades upp får fokus stanna där.
  moveFocusTo(element) {
    setTimeout(() => {
      const active = document.activeElement;
      const lost = !active || active === document.body || active === this.browse || active === this.removeButton;
      if (lost) element?.focus();
    });
  }

  getType(t) {
    if (t.toLowerCase().startsWith("image")) return "Image";
    if (t.toLowerCase().endsWith("pdf")) return "Document";
    return "Other";
  }
}
